"use client";

import { MAX_SEARCH_DEPTH } from "@/components/library/document-permissions/constants";
import Spinner from "@/components/shared/spinner";
import { useAuth, useOrganization, useUser } from "@clerk/nextjs";
import axios from "axios";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";
import useSWR, { KeyedMutator } from "swr";
import useSWRImmutable from "swr/immutable";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useSupabaseClient } from "./supabase/supabase-client";

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

const DocIdContext = createContext<{
  docIds: string[];
  existingWorkspaceSettings: Record<string, any>;
  isWorkspaceSettingsLoading: boolean;
  mutateWorkspaceSettings: KeyedMutator<any>;
  latestPath: string | null;
  industry: any[];
  subscriptions: any[];
  subscriptionsLoading: boolean;
  isBasicPlan: boolean;
  isTeamPlan: boolean;
  isBusinessPlan: boolean;
  isBusinessPlanLoaded: boolean;
  drivers?: any[];
  suggestedDrivers?: any[];
  dealFilters: any;
  bookmarkedDocumentOrEventIds: string[];
  products: any[];
  competitors: any[];
  segments: any[];
}>({
  docIds: [],
  existingWorkspaceSettings: {},
  isWorkspaceSettingsLoading: true,
  mutateWorkspaceSettings: async () => {},
  latestPath: null,
  industry: [],
  subscriptions: [],
  subscriptionsLoading: true,
  isBasicPlan: false,
  isTeamPlan: false,
  isBusinessPlan: false,
  isBusinessPlanLoaded: false,
  drivers: [],
  suggestedDrivers: [],
  dealFilters: [],
  bookmarkedDocumentOrEventIds: [],
  products: [],
  competitors: [],
  segments: [],
});

export const DocIdContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { userId, orgId } = useAuth();
  const businessPlanEnabled = useFeatureFlagEnabled("business-plan");
  const pathname = usePathname();
  const { organization } = useOrganization();

  // On public auth pages (/sign-in, /sign-up) the server has no valid session,
  // so authenticated API calls get 307-redirected to /sign-in and return HTML.
  // Skip all fetches there even if client-side Clerk still has a stale orgId.
  const isAuthPage =
    pathname?.startsWith("/sign-in") || pathname?.startsWith("/sign-up");
  const shouldFetch = !!orgId && !isAuthPage;

  const {
    data: existingWorkspaceSettings,
    mutate: mutateWorkspaceSettings,
    isLoading: isWorkspaceSettingsLoading,
  } = useSWR(shouldFetch ? `/api/workspace-settings` : null, fetcher);

  const { data: clientIndustry, isLoading: isClientIndustryLoading } = useSWR(
    shouldFetch ? `/api/clients/industry` : null,
    fetcher,
  );
  const { data: dealFilters, isLoading: dealFiltersLoading } = useSWR(
    shouldFetch ? `/api/deals/filters` : null,
    fetcher,
  );
  const {
    data: existingReasons,
    mutate: reasonsMutate,
    isLoading: reasonsLoading,
  } = useSWR(shouldFetch ? "/api/reasons?includeSuggested=true" : null, fetcher);
  const { data: subscriptions, isLoading: subscriptionsLoading } = useSWR(
    shouldFetch
      ? `/api/create-subscription?noProductName=true&orgId=${orgId}`
      : null,
    fetcher,
  );
  const { data: products } = useSWR(
    shouldFetch ? "/api/products" : null,
    fetcher,
  );
  const { data: competitors } = useSWR(
    shouldFetch ? "/api/competitors" : null,
    fetcher,
  );
  const { data: segments } = useSWR(
    shouldFetch ? "/api/segments" : null,
    fetcher,
  );
  
  const [latestPath, setLatestPath] = useState<string | null>(null);
  const [bookmarkedDocumentOrEventIds, setBookmarkedDocumentOrEventIds] =
    useState<string[]>([]);
  
  useEffect(() => {
    if (!pathname?.includes("/settings")) {
      setLatestPath(pathname);
    }
  }, [pathname]);
  
  
  if (!userId || !orgId) {
    return <>{children}</>;
  }
  return (
    <DocIdContext.Provider
      value={{
        docIds: [],
        existingWorkspaceSettings: existingWorkspaceSettings || {},
        isWorkspaceSettingsLoading: isWorkspaceSettingsLoading,
        mutateWorkspaceSettings: mutateWorkspaceSettings,
        latestPath: latestPath,
        industry: clientIndustry?.industries?.filter((i: any) => i) || [],
        drivers: Array.isArray(existingReasons)
          ? existingReasons.filter((r: any) => !r.suggested)
          : [],
        suggestedDrivers: Array.isArray(existingReasons)
          ? existingReasons.filter((r: any) => r.suggested)
          : [],
        subscriptions: subscriptions || [],
        subscriptionsLoading: subscriptionsLoading,
        isBasicPlan:
          subscriptions?.[0]?.plan?.id ===
          process.env.NEXT_PUBLIC_DEALPAGE_BASICS_PRICE_ID,
        isTeamPlan:
          subscriptions?.[0]?.plan?.id ===
          process.env.NEXT_PUBLIC_DEALPAGE_TEAM_PRICE_ID,
        isBusinessPlan: businessPlanEnabled === true,
        isBusinessPlanLoaded: businessPlanEnabled !== undefined,
        dealFilters: dealFilters || {},
        bookmarkedDocumentOrEventIds: bookmarkedDocumentOrEventIds,
        products: products || [],
        competitors: competitors || [],
        segments: segments || [],
      }}
    >
      {children}
    </DocIdContext.Provider>
  );
};

export function useDocIdContext() {
  return useContext(DocIdContext);
}
